import { Alert, Link, Box, Container } from "@mui/material";
import React from "react";
import { useState, useEffect } from "react";
import { useSubscriptionAccessContext } from "../contexts/SubscriptionAccessContext";
import { Link as RouterLink } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useIsAuthenticated } from "@auth/hooks/useIsAuthenticated";
import { RouteTo, RouteToSettings } from "@routing/RouteMap";
import { useTheme } from "@mui/material/styles";
import { navbarHeight } from "@core/layout/AppLayout";

/**
 * Globaler Banner, der bei fehlendem Abo oder abgelaufenem Abo angezeigt wird.
 * Wird in AppLayout an zentraler Stelle gerendert.
 */
const SubscriptionBanner = () => {
  // Delay state: banner will only appear after a short timeout to avoid brief flicker
  const [shouldRender, setShouldRender] = useState(false);
  const theme = useTheme();
  const { pathname } = useLocation();
  const { loading, hasAccess, isExpired, isInGracePeriod } = useSubscriptionAccessContext();

  // Banner should only be shown to authenticated users
  const isAuthenticated = useIsAuthenticated();

  // Hide banner on shop-related pages to avoid duplicate upgrade messaging
  // Define all routes where the banner should NOT be shown (exact match or prefix match)
  const excludedRoutes = [
    RouteTo.LOGIN,
    RouteTo.INVITE,
    RouteTo.FORGOT_PASSWORD,
    RouteTo.RESET_PASSWORD,
    RouteTo.ONBOARDING,
    RouteTo.SHOP,
    RouteTo.PRIVACY,
    RouteTo.LEGAL,
    RouteTo.AGB,
    RouteTo.USER_GUIDE,
    RouteTo.CONTACT,
  ];

  // Also exclude shop-related settings subroutes
  const excludedSettingsRoutes = [
    RouteToSettings.SHOP_AI_SERVICE,
    RouteToSettings.SHOP_CONSULTING,
    // RouteToSettings.SHOP_EXTRA_PAGES,
    // RouteToSettings.SHOP_SAAS,
  ];

  // For routes like /invite/:code, we want to exclude any path that starts with /invite
  // This works because pathname.startsWith(RouteTo.INVITE) will be true for both "/invite" and "/invite/xyz"
  const isExcluded =
    [...excludedRoutes, ...excludedSettingsRoutes]
      .filter(Boolean)
      .some((route) => pathname.startsWith(route));

  // Manage the delayed visibility of the banner. We want to avoid showing it
  // immediately after login in cases where the subscription query is still in
  // flight or the state might quickly change.
  useEffect(() => {
    // Reset visibility when conditions that require hiding the banner apply.
    if (loading || hasAccess) {
      setShouldRender(false);
      return;
    }

    // If banner should be shown (no access, not loading), start the delay.
    const timeout = setTimeout(() => {
      setShouldRender(true);
    }, 2000); // 2-second delay – adjust if desired

    return () => clearTimeout(timeout);
  }, [loading, hasAccess]);

  if (isExcluded || !isAuthenticated) {
    return null;
  }

  // While loading or during delay, render nothing.
  if (loading || !shouldRender) {
    return null;
  }

  // Shared wrapper styles: fixed just below the navbar
  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
      sx={{
        position: "fixed",
        top: navbarHeight,
        left: 0,
        width: "100%",
        zIndex: theme.zIndex.appBar - 1, // directly beneath AppBar
        bgcolor: theme.palette.background.paper, // ensure opaque background
        boxShadow: theme.shadows[2],
        pointerEvents: "none", // allow clicks to pass through unless on banner itself
      }}
    >
      <Container maxWidth="xl" sx={{ pointerEvents: "auto" }}>
        {children}
      </Container>
    </Box>
  );

  // 2. Aktives Abo → Kein Banner nötig
  if (hasAccess) return null;

  // 3. Kein Abo oder abgelaufen → warnen
  const message = isExpired
    ? "Dein Abonnement ist abgelaufen. Bitte verlängere, um alle Funktionen weiter zu nutzen."
    : isInGracePeriod
    ? "Deine Zahlung steht aus. Bitte aktualisiere sie, um Unterbrechungen zu vermeiden."
    : "Du benötigst ein aktives Abonnement, um alle Funktionen von ESGbot zu nutzen.";

  return (
    <Wrapper>
      <Alert severity="warning">
        {message} {" "}
        <Link component={RouterLink} to={RouteTo.SHOP} underline="hover">
          Zum Abo&nbsp;Upgrade
        </Link>
      </Alert>
    </Wrapper>
  );
};

export default SubscriptionBanner; 